const STORAGE_KEY = 'flashai-auth';
const API_BASE = '/api';

// Nginx Basic Auth credentials (from environment variables)
const NGINX_USER = process.env.REACT_APP_NGINX_USER || '';
const NGINX_PASS = process.env.REACT_APP_NGINX_PASS || '';

// Build Basic Auth header
function getBasicAuthHeader() {
  if (NGINX_USER && NGINX_PASS) {
    const credentials = btoa(`${NGINX_USER}:${NGINX_PASS}`);
    return `Basic ${credentials}`;
  }
  return null;
}

const defaultHeaders = {
  'Content-Type': 'application/json',
};

export function loadSession() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch (err) {
    console.warn('Failed to parse session', err);
    return null;
  }
}

export function saveSession(session) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(STORAGE_KEY);
}

function buildHeaders(token, extra = {}) {
  const headers = { ...defaultHeaders, ...extra };
  
  // Add Basic Auth for Nginx (if configured)
  const basicAuth = getBasicAuthHeader();
  if (basicAuth) {
    // Nginx requires Basic Auth in Authorization header
    headers['Authorization'] = basicAuth;
    
    // Add JWT token in a separate header that nginx will forward
    // Use both X-Access-Token and X-Authorization for compatibility
    // (Some nginx configs might forward one but not the other)
    if (token) {
      headers['X-Access-Token'] = token;
      headers['X-Authorization'] = `Bearer ${token}`;
    }
  } else if (token) {
    // No Basic Auth configured, use Bearer token as normal
    headers.Authorization = `Bearer ${token}`;
  }
  
  return headers;
}

async function request(path, { method = 'GET', body, token, headers = {} } = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: buildHeaders(token, headers),
    body: body ? JSON.stringify(body) : undefined,
  });

  if (response.status === 204) {
    return null;
  }

  const text = await response.text().catch(() => '');
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (err) {
      data = null;
    }
  }
  if (!response.ok) {
    const message =
      data?.detail ||
      data?.message ||
      (typeof data === 'string' ? data : null) ||
      text ||
      'Request failed';
    const error = new Error(message);
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  if (data !== null) {
    return data;
  }
  return text || null;
}

export async function login({ username, password }) {
  return request('/auth/login/', {
    method: 'POST',
    body: { username, password },
  });
}

export async function logout(token, refreshToken) {
  return request('/auth/logout/', {
    method: 'POST',
    token,
    body: refreshToken ? { refresh: refreshToken } : {},
  });
}

export async function fetchProfile(token) {
  return request('/auth/me/', { token });
}

export async function fetchCustomers(token) {
  return request('/admin/customers/', { token });
}

export async function fetchCustomerSummary(token) {
  return request('/customer/me/', { token });
}

export async function fetchSavedQueries(token) {
  return request('/saved-queries/', { token });
}

export async function saveSavedQuery(token, query) {
  return request('/saved-queries/', {
    method: 'POST',
    token,
    body: { query },
  });
}

export async function deleteSavedQuery(token, queryId) {
  return request(`/saved-queries/${queryId}/`, {
    method: 'DELETE',
    token,
  });
}

export async function createCustomer(token, payload) {
  return request('/admin/customers/', {
    method: 'POST',
    token,
    body: payload,
  });
}

export async function updateCustomer(token, customerId, payload) {
  return request(`/admin/customers/${customerId}/`, {
    method: 'PATCH',
    token,
    body: payload,
  });
}

export async function deleteCustomer(token, customerId) {
  return request(`/admin/customers/${customerId}/`, {
    method: 'DELETE',
    token,
  });
}

export async function listCustomerAdmins(token, customerId) {
  return request(`/admin/customers/${customerId}/admins/`, { token });
}

export async function createCustomerAdmin(token, customerId, payload) {
  return request(`/admin/customers/${customerId}/admins/`, {
    method: 'POST',
    token,
    body: payload,
  });
}

export async function updateCustomerAdmin(token, customerId, adminId, payload) {
  return request(`/admin/customers/${customerId}/admins/${adminId}/`, {
    method: 'PATCH',
    token,
    body: payload,
  });
}

export async function deleteCustomerAdmin(token, customerId, adminId) {
  return request(`/admin/customers/${customerId}/admins/${adminId}/`, {
    method: 'DELETE',
    token,
  });
}

export async function fetchCustomerUsers(token, customerId, params = {}) {
  const query = new URLSearchParams(params).toString();
  const path = `/customers/${customerId}/users${query ? `?${query}` : ''}`;
  return request(path, { token });
}

export async function createCustomerUser(token, customerId, payload) {
  return request(`/customers/${customerId}/users/`, {
    method: 'POST',
    token,
    body: payload,
  });
}

export async function updateCustomerUser(token, customerId, userId, payload) {
  return request(`/customers/${customerId}/users/${userId}/`, {
    method: 'PATCH',
    token,
    body: payload,
  });
}

export async function deleteCustomerUser(token, customerId, userId) {
  return request(`/customers/${customerId}/users/${userId}/`, {
    method: 'DELETE',
    token,
  });
}

export async function fetchLicenseUsage(token) {
  return request('/admin/license-usage/', { token });
}

export async function fetchAuditLogs(token) {
  return request('/audit-logs/', { token });
}

export async function forceLogout(token, accountId) {
  return request(`/sessions/${accountId}/force-logout/`, {
    method: 'POST',
    token,
  });
}

export async function renewCustomer(token, customerId, expiryDate) {
  return request(`/admin/customers/${customerId}/renew/`, {
    method: 'POST',
    token,
    body: { expiry_date: expiryDate },
  });
}

export async function acceptConsent({ username, password }) {
  return request('/auth/accept-consent/', {
    method: 'POST',
    body: { username, password },
  });
}

